import { PaymentPlan, PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { CurrentTierUsage, getLimitFromString, LimitKeys } from 'api/clients/TierUsageClient'
import { useCallback, useEffect } from 'react'
import { useRootStore } from 'stores/RootStore'
import { useCurrentWorkspace } from './useCurrentWorkspace'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'

export const useCanChangeTier = () => {
  const tierUsageStore = useRootStore().tierUsageStore
  const workspace = useCurrentWorkspace()

  const { tierInfo, currentSubscription, paymentPlans, initialized, loading } = tierUsageStore
  const currentTierPlan = paymentPlans?.find((plan) => plan.key === currentSubscription?.plan)

  useEffect(() => {
    if (!initialized && !loading && workspace) {
      tierUsageStore.fetch(workspace.id)
    }
  }, [initialized, loading, tierUsageStore, workspace])

  const checkCanDowngradeToTier = useCallback(
    (downgradeTier: PaymentPlanKey) => {
      const downgradePlan = paymentPlans?.find((plan) => plan.key === downgradeTier)

      if (!downgradePlan || !currentTierPlan || !tierInfo) {
        return undefined
      }
      return canDowngradeToTier(currentTierPlan, downgradePlan, tierInfo)
    },
    [currentTierPlan, paymentPlans, tierInfo],
  )

  return checkCanDowngradeToTier
}

const canDowngradeToTier = (currentPlan: PaymentPlan, downgradePlan: PaymentPlan, currentUsage: CurrentTierUsage) => {
  const limitKeys: LimitKeys[] = ['seats', 'brand_kits', 'source_materials']
  const integrationKeys = [
    ExternalIntegrationEnum.WordPress,
    ExternalIntegrationEnum.X,
    ExternalIntegrationEnum.LinkedIn,
    ExternalIntegrationEnum.Instagram,
    ExternalIntegrationEnum.Facebook,
    ExternalIntegrationEnum.TikTok,
  ]

  for (const key of limitKeys) {
    const limit = getLimitFromString(downgradePlan.base_price_meta?.[`${key}_limit`])

    if (currentUsage[key] > limit) {
      return false
    }
  }

  for (const key of integrationKeys) {
    const integrationsLimit = getLimitFromString(downgradePlan.base_price_meta?.integrations_limit)

    if (currentUsage.integrations[key] > integrationsLimit) {
      return false
    }
  }

  return true
}
